import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Calendar, Users, Clock, CheckCircle, Loader2, Send, ChevronDown, Upload, X, IndianRupee,
} from 'lucide-react';
import logoImg from '../../assets/logo.png';
import { decryptBookingParams } from '../../utils/linkCrypto';
import { API_BASE_URL } from '../../services/api';
import './PublicBookingForm.css';

const API_BASE = API_BASE_URL;

const PublicBookingForm = () => {
  const { facilityId, serviceId } = useParams();
  const [searchParams] = useSearchParams();
  const encToken = searchParams.get('d');
  const decoded = encToken ? decryptBookingParams(encToken) : null;
  const qsSlotId = decoded?.slotId ?? null;
  const qsDate = decoded?.date ?? null;
  const qsBookingType = decoded?.bookingType ?? null;
  const qsPrice = decoded?.price ?? null;
  const qsAdvance = decoded?.advance ?? null;

  const locked = {
    date: !!qsDate,
    slot: !!qsSlotId,
    bookingType: !!qsBookingType,
    price: !!qsPrice,
    advance: !!qsAdvance,
  };

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedRef, setSubmittedRef] = useState('');
  const receiptInputRef = useRef(null);

  const [form, setForm] = useState({
    date: qsDate || '',
    selectedSlotId: qsSlotId ? Number(qsSlotId) : null,
    selectedSlotLabel: '',
    bookingType: qsBookingType || 'single',
    teamAName: '', teamACaptain: '', teamAPhone: '',
    teamBName: '', teamBCaptain: '', teamBPhone: '',
    contactName: '', contactPhone: '',
    specialRequests: '',
    ballType: 'leather',
    ballColor: 'red',
    umpire: '2',
    oversCount: 20,
  });

  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [matchConfigOpen, setMatchConfigOpen] = useState(false);
  const [specialReqOpen, setSpecialReqOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/booking-requests/public/info/${facilityId}/${serviceId}`);
        const data = await res.json();
        if (!data.success) throw new Error(data.message || 'Not found');
        setInfo(data.data);

        if (!qsDate) {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          setForm((f) => ({ ...f, date: tomorrow.toISOString().split('T')[0] }));
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [facilityId, serviceId, qsDate]);

  const isMatch = info?.service_type === 'cricket_match';

  const validate = () => {
    const e = {};
    if (!form.date) e.date = 'Date is required';
    if (!form.teamAName.trim()) e.teamAName = 'Team name is required';
    if (!form.teamACaptain.trim()) e.teamACaptain = 'Captain name is required';
    if (!form.teamAPhone || !/^\d{10}$/.test(form.teamAPhone)) {
      e.teamAPhone = 'Enter a valid 10-digit number';
    }
    if (form.bookingType === 'both') {
      if (!form.teamBName.trim()) e.teamBName = 'Team B name is required';
      if (!form.teamBCaptain.trim()) e.teamBCaptain = 'Team B captain is required';
      if (form.teamBPhone && !/^\d{10}$/.test(form.teamBPhone)) {
        e.teamBPhone = 'Enter a valid 10-digit number';
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, receipt: 'File must be under 5 MB' }));
      return;
    }
    setReceiptFile(file);
    setErrors((prev) => ({ ...prev, receipt: undefined }));
    const reader = new FileReader();
    reader.onload = (ev) => setReceiptPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeReceipt = () => {
    setReceiptFile(null);
    setReceiptPreview(null);
    if (receiptInputRef.current) receiptInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const selectedSlot = info?.slots?.find((s) => s.id === form.selectedSlotId);

      const fd = new FormData();
      fd.append('facility_id', Number(facilityId));
      fd.append('ground_id', info.ground_id);
      fd.append('service_id', Number(serviceId));
      if (form.selectedSlotId) fd.append('slot_id', form.selectedSlotId);
      fd.append('date', form.date);
      if (selectedSlot) {
        fd.append('time_slot', `${selectedSlot.start_time?.slice(0, 5)}-${selectedSlot.end_time?.slice(0, 5)}`);
        fd.append('slot_name', selectedSlot.name || '');
      }
      fd.append('service_type', info.service_type);
      fd.append('service_category', info.service_category);
      fd.append('booking_type', form.bookingType);
      fd.append('team_a_name', form.teamAName);
      fd.append('team_a_captain_name', form.teamACaptain);
      fd.append('team_a_captain_phone', form.teamAPhone);
      if (form.bookingType === 'both') {
        fd.append('team_b_name', form.teamBName);
        fd.append('team_b_captain_name', form.teamBCaptain);
        fd.append('team_b_captain_phone', form.teamBPhone);
      }
      fd.append('contact_name', form.contactName || form.teamACaptain);
      fd.append('contact_phone', form.contactPhone || form.teamAPhone);
      if (form.specialRequests) fd.append('special_requests', form.specialRequests);
      if (qsPrice) fd.append('price', qsPrice);
      if (qsAdvance) fd.append('advance_amount', qsAdvance);
      if (isMatch) {
        fd.append('match_details', JSON.stringify({
          ballType: form.ballType,
          ballColor: form.ballColor,
          umpire: form.umpire,
          oversCount: form.oversCount,
        }));
      }
      if (receiptFile) fd.append('receipt', receiptFile);

      const res = await fetch(`${API_BASE}/booking-requests/public/submit`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Submission failed');
      setSubmittedRef(data.data.reference);
      setSubmitted(true);
    } catch (e) {
      setErrors((prev) => ({ ...prev, submit: e.message }));
    } finally {
      setSubmitting(false);
    }
  };

  const set = (key, val) => {
    setForm((f) => ({ ...f, [key]: val }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  /* ── Loading / Error ──────────────────────────────────────── */
  if (loading) {
    return (
      <div className="pbf-page">
        <div className="pbf-loading">
          <Loader2 size={36} className="pbf-spin" style={{ margin: '0 auto 1rem', display: 'block' }} />
          <p>Loading booking form&hellip;</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pbf-page">
        <div className="pbf-container" style={{ textAlign: 'center', paddingTop: '4rem' }}>
          <h2 style={{ color: '#ef4444', marginBottom: '0.5rem' }}>Oops!</h2>
          <p style={{ color: '#6b7280' }}>{error}</p>
        </div>
      </div>
    );
  }

  /* ── Success ──────────────────────────────────────────────── */
  if (submitted) {
    return (
      <div className="pbf-page">
        <Header facilityName={info.facility_name} />
        <div className="pbf-container">
          <div className="pbf-success-card">
            <CheckCircle size={56} className="pbf-success-icon" />
            <h2>Request Submitted!</h2>
            <p>
              Your booking request has been sent to <strong>{info.facility_name}</strong>.
              You&apos;ll receive a confirmation on WhatsApp once it&apos;s approved.
            </p>
            <div className="pbf-ref">{submittedRef}</div>
            <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#9ca3af' }}>
              Save this reference for your records.
            </p>
          </div>
        </div>
      </div>
    );
  }

  /* ── Form ─────────────────────────────────────────────────── */
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="pbf-page">
      <Header facilityName={info.facility_name} />

      <div className="pbf-container">
        <div className="pbf-hero">
          <h1>Book a Session</h1>
          <p>{info.service_name} &mdash; {info.ground_name}</p>
        </div>

        {/* Date & Slot */}
        <div className="pbf-card">
          <div className="pbf-card-title"><Calendar size={18} /> Date &amp; Time Slot</div>

          <div className="pbf-field">
            <label className="pbf-label">Preferred Date *</label>
            {locked.date ? (
              <div className="pbf-locked-value">{form.date}</div>
            ) : (
              <input
                type="date"
                className={`pbf-input ${errors.date ? 'pbf-input-error' : ''}`}
                min={today}
                value={form.date}
                onChange={(e) => set('date', e.target.value)}
              />
            )}
            {errors.date && <div className="pbf-error">{errors.date}</div>}
          </div>

          {info.slots?.length > 0 && (
            <div className="pbf-field">
              <label className="pbf-label">Time Slot</label>
              {locked.slot ? (
                <div className="pbf-locked-value">
                  {(() => {
                    const s = info.slots.find((sl) => sl.id === form.selectedSlotId);
                    return s ? `${s.name} (${s.start_time?.slice(0, 5)} - ${s.end_time?.slice(0, 5)})` : `Slot #${form.selectedSlotId}`;
                  })()}
                </div>
              ) : (
                <div className="pbf-slot-grid">
                  {info.slots.map((slot) => (
                    <button
                      key={slot.id}
                      type="button"
                      className={`pbf-slot-btn ${form.selectedSlotId === slot.id ? 'selected' : ''}`}
                      onClick={() => set('selectedSlotId', slot.id)}
                    >
                      <span className="pbf-slot-name">{slot.name}</span>
                      <span className="pbf-slot-time">
                        {slot.start_time?.slice(0, 5)} - {slot.end_time?.slice(0, 5)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Booking Type (match only) */}
        {isMatch && (
          <div className="pbf-card">
            <div className="pbf-card-title"><Users size={18} /> Booking Type</div>
            {locked.bookingType ? (
              <div className="pbf-locked-value">
                {form.bookingType === 'both' ? 'Both Teams' : 'Single Team'}
              </div>
            ) : (
              <div className="pbf-type-row">
                <button
                  type="button"
                  className={`pbf-type-btn ${form.bookingType === 'both' ? 'active' : ''}`}
                  onClick={() => set('bookingType', 'both')}
                >
                  Book Both Teams
                </button>
                <button
                  type="button"
                  className={`pbf-type-btn ${form.bookingType === 'single' ? 'active' : ''}`}
                  onClick={() => set('bookingType', 'single')}
                >
                  Single Team
                </button>
              </div>
            )}
          </div>
        )}

        {/* Price & Advance (read-only when provided via link) */}
        {(locked.price || locked.advance) && (
          <div className="pbf-card">
            <div className="pbf-card-title"><IndianRupee size={18} /> Payment Summary</div>
            <div className="pbf-price-grid">
              {locked.price && (
                <div className="pbf-price-item">
                  <span className="pbf-price-label">Total Amount</span>
                  <span className="pbf-price-value">{'\u20B9'}{Number(qsPrice).toLocaleString('en-IN')}</span>
                </div>
              )}
              {locked.advance && (
                <div className="pbf-price-item">
                  <span className="pbf-price-label">Advance Amount</span>
                  <span className="pbf-price-value">{'\u20B9'}{Number(qsAdvance).toLocaleString('en-IN')}</span>
                </div>
              )}
              {locked.price && locked.advance && (
                <div className="pbf-price-item pbf-price-balance">
                  <span className="pbf-price-label">Balance Due</span>
                  <span className="pbf-price-value">{'\u20B9'}{(Number(qsPrice) - Number(qsAdvance)).toLocaleString('en-IN')}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Team Details */}
        <div className="pbf-card">
          <div className="pbf-card-title"><Users size={18} /> Team Details</div>

          <div className="pbf-team-block">
            <div className="pbf-team-label">Team A</div>
            <div className="pbf-field">
              <label className="pbf-label">Team Name *</label>
              <input
                className={`pbf-input ${errors.teamAName ? 'pbf-input-error' : ''}`}
                placeholder="e.g. Mumbai Warriors"
                value={form.teamAName}
                onChange={(e) => set('teamAName', e.target.value)}
              />
              {errors.teamAName && <div className="pbf-error">{errors.teamAName}</div>}
            </div>
            <div className="pbf-row">
              <div className="pbf-field">
                <label className="pbf-label">Captain Name *</label>
                <input
                  className={`pbf-input ${errors.teamACaptain ? 'pbf-input-error' : ''}`}
                  placeholder="Captain name"
                  value={form.teamACaptain}
                  onChange={(e) => set('teamACaptain', e.target.value)}
                />
                {errors.teamACaptain && <div className="pbf-error">{errors.teamACaptain}</div>}
              </div>
              <div className="pbf-field">
                <label className="pbf-label">Mobile *</label>
                <input
                  className={`pbf-input ${errors.teamAPhone ? 'pbf-input-error' : ''}`}
                  placeholder="10-digit number"
                  maxLength={10}
                  value={form.teamAPhone}
                  onChange={(e) => set('teamAPhone', e.target.value.replace(/\D/g, ''))}
                />
                {errors.teamAPhone && <div className="pbf-error">{errors.teamAPhone}</div>}
              </div>
            </div>
          </div>

          {form.bookingType === 'both' && (
            <div className="pbf-team-block">
              <div className="pbf-team-label">Team B</div>
              <div className="pbf-field">
                <label className="pbf-label">Team Name *</label>
                <input
                  className={`pbf-input ${errors.teamBName ? 'pbf-input-error' : ''}`}
                  placeholder="e.g. Delhi Chargers"
                  value={form.teamBName}
                  onChange={(e) => set('teamBName', e.target.value)}
                />
                {errors.teamBName && <div className="pbf-error">{errors.teamBName}</div>}
              </div>
              <div className="pbf-row">
                <div className="pbf-field">
                  <label className="pbf-label">Captain Name *</label>
                  <input
                    className={`pbf-input ${errors.teamBCaptain ? 'pbf-input-error' : ''}`}
                    placeholder="Captain name"
                    value={form.teamBCaptain}
                    onChange={(e) => set('teamBCaptain', e.target.value)}
                  />
                  {errors.teamBCaptain && <div className="pbf-error">{errors.teamBCaptain}</div>}
                </div>
                <div className="pbf-field">
                  <label className="pbf-label">Mobile</label>
                  <input
                    className={`pbf-input ${errors.teamBPhone ? 'pbf-input-error' : ''}`}
                    placeholder="10-digit number"
                    maxLength={10}
                    value={form.teamBPhone}
                    onChange={(e) => set('teamBPhone', e.target.value.replace(/\D/g, ''))}
                  />
                  {errors.teamBPhone && <div className="pbf-error">{errors.teamBPhone}</div>}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Match Configuration (cricket matches only) */}
        {isMatch && (
          <div className="pbf-card" style={{ padding: 0, overflow: 'hidden' }}>
            <button
              type="button"
              onClick={() => setMatchConfigOpen((o) => !o)}
              style={{
                width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '0.85rem 1.25rem', border: 'none', background: 'transparent', cursor: 'pointer',
                fontSize: '0.9rem', fontWeight: 700, color: '#374151',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={18} style={{ color: '#16a34a' }} /> Match Configuration
              </span>
              <ChevronDown size={18} style={{ transform: matchConfigOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
            </button>
            {matchConfigOpen && (
              <div style={{ padding: '0 1.25rem 1rem' }}>
                <div className="pbf-field">
                  <label className="pbf-label">Ball Type</label>
                  <div className="pbf-type-row">
                    {['leather', 'tennis'].map((bt) => (
                      <button
                        key={bt} type="button"
                        className={`pbf-type-btn ${form.ballType === bt ? 'active' : ''}`}
                        onClick={() => set('ballType', bt)}
                      >
                        {bt.charAt(0).toUpperCase() + bt.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="pbf-field">
                  <label className="pbf-label">Ball Color</label>
                  <div className="pbf-type-row">
                    {['red', 'white'].map((bc) => (
                      <button
                        key={bc} type="button"
                        className={`pbf-type-btn ${form.ballColor === bc ? 'active' : ''}`}
                        onClick={() => set('ballColor', bc)}
                      >
                        {bc.charAt(0).toUpperCase() + bc.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="pbf-row">
                  <div className="pbf-field">
                    <label className="pbf-label">Umpires</label>
                    <div className="pbf-type-row">
                      {['1', '2'].map((u) => (
                        <button
                          key={u} type="button"
                          className={`pbf-type-btn ${form.umpire === u ? 'active' : ''}`}
                          onClick={() => set('umpire', u)}
                        >
                          {u} Umpire{u === '2' ? 's' : ''}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="pbf-field">
                    <label className="pbf-label">Overs</label>
                    <input
                      className="pbf-input"
                      type="number" min="5" max="50"
                      value={form.oversCount}
                      onChange={(e) => set('oversCount', parseInt(e.target.value) || 20)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Special Requests */}
        <div className="pbf-card" style={{ padding: 0, overflow: 'hidden' }}>
          <button
            type="button"
            onClick={() => setSpecialReqOpen((o) => !o)}
            style={{
              width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '0.85rem 1.25rem', border: 'none', background: 'transparent', cursor: 'pointer',
              fontSize: '0.9rem', fontWeight: 600, color: '#374151',
            }}
          >
            Special Requests
            <ChevronDown size={18} style={{ transform: specialReqOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
          </button>
          {specialReqOpen && (
            <div style={{ padding: '0 1.25rem 1rem' }}>
              <textarea
                className="pbf-input"
                rows={3}
                placeholder="Any special instructions for the facility..."
                value={form.specialRequests}
                onChange={(e) => set('specialRequests', e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </div>
          )}
        </div>

        {/* Payment Receipt Upload */}
        <div className="pbf-card">
          <div className="pbf-card-title"><Upload size={18} /> Upload Payment Receipt</div>
          <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '-0.5rem 0 0.75rem' }}>
            Upload a screenshot or photo of your payment (UPI, bank transfer, etc.)
          </p>

          {receiptPreview ? (
            <div className="pbf-receipt-preview">
              <img src={receiptPreview} alt="Payment receipt" className="pbf-receipt-img" />
              <button type="button" className="pbf-receipt-remove" onClick={removeReceipt}>
                <X size={16} /> Remove
              </button>
            </div>
          ) : (
            <label className="pbf-upload-area">
              <Upload size={28} style={{ color: '#9ca3af', marginBottom: '0.5rem' }} />
              <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                Click to upload receipt
              </span>
              <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
                JPG, PNG or PDF &bull; Max 5 MB
              </span>
              <input
                ref={receiptInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={handleReceiptChange}
                style={{ display: 'none' }}
              />
            </label>
          )}
          {errors.receipt && <div className="pbf-error" style={{ marginTop: '0.5rem' }}>{errors.receipt}</div>}
        </div>

        {/* Submit */}
        {errors.submit && (
          <div className="pbf-error" style={{ textAlign: 'center', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
            {errors.submit}
          </div>
        )}

        <button
          type="button"
          className="pbf-submit-btn"
          disabled={submitting}
          onClick={handleSubmit}
        >
          {submitting ? <Loader2 size={20} className="pbf-spin" /> : <Send size={18} />}
          {submitting ? 'Submitting...' : 'Submit Booking Request'}
        </button>

        <p style={{ textAlign: 'center', fontSize: '0.7rem', color: '#9ca3af', marginTop: '1rem' }}>
          Powered by <strong>Ground Aale</strong>
        </p>
      </div>
    </div>
  );
};

function Header({ facilityName }) {
  return (
    <div className="pbf-header">
      <div className="pbf-header-brand">
        <img src={logoImg} alt="Ground Aale" className="pbf-header-logo" />
      </div>
      <span className="pbf-header-badge">{facilityName}</span>
    </div>
  );
}

export default PublicBookingForm;
